
import React, { useState } from 'react';

const Node: React.FC<{ 
  title: string; 
  id: string; 
  desc: string; 
  isActive: boolean; 
  onHover: () => void;
  onLeave: () => void;
}> = ({ title, id, desc, isActive, onHover, onLeave }) => (
  <div 
    onMouseEnter={onHover}
    onMouseLeave={onLeave}
    onClick={onHover}
    className={`p-6 sm:p-8 rounded-2xl glass-card border transition-all duration-500 cursor-crosshair relative group ${isActive ? 'border-[#32dee6] shadow-[0_0_50px_rgba(50,222,230,0.15)] scale-[1.02] ring-1 ring-[#32dee6]/50 z-40' : 'border-white/5 opacity-60 hover:opacity-100 hover:border-white/20 z-10'}`}
  >
    <div className="relative">
      <div className={`mono text-[8px] sm:text-[9px] mb-2 sm:mb-3 uppercase tracking-[0.2em] font-bold transition-colors ${isActive ? 'text-[#32dee6]' : 'text-gray-500'}`}>Logic_Gate_{id}</div>
      <h4 className={`text-sm sm:text-base font-black uppercase tracking-tighter transition-colors leading-tight ${isActive ? 'text-white' : 'text-gray-300'}`}>{title}</h4>
    </div>
    
    {isActive && (
      <div className="absolute top-full left-1/2 -translate-x-1/2 sm:left-6 sm:translate-x-0 w-[280px] sm:w-80 mt-8 p-5 sm:p-6 rounded-2xl bg-black/95 backdrop-blur-xl border border-[#32dee6]/40 shadow-[0_20px_60px_rgba(0,0,0,0.8)] z-50 animate-slide-up pointer-events-auto text-left">
        {/* Vertical Line Connector - centered under the title's content area */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 sm:left-8 sm:translate-x-0 w-[1px] h-8 bg-gradient-to-b from-[#32dee6]/60 to-[#32dee6]/40"></div>
        {/* Terminal point on the box side */}
        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 sm:left-8 sm:translate-x-0 w-1 h-1 rounded-full bg-[#32dee6]"></div>
        
        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light">{desc}</p>
        <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#32dee6] animate-pulse"></div>
            <span className="mono text-[8px] sm:text-[9px] text-[#32dee6] uppercase font-bold tracking-widest">Execution: Instant</span>
          </div>
          <span className="mono text-[7px] sm:text-[8px] text-gray-600 uppercase font-bold tracking-widest">v4.0_Core</span>
        </div>
      </div>
    )}
  </div>
);

const CallFlowVisualizer: React.FC = () => {
  const [activeNode, setActiveNode] = useState<string | null>(null);

  const nodes = [
    { 
      id: '01', 
      title: '1. YOU MISS A CALL', 
      desc: "You're under a house or driving to a site. The phone rings, but you can't answer. No more missed opportunity logic starts here." 
    },
    { 
      id: '02', 
      title: '2. WE STEP IN', 
      desc: 'Our system instantly texts back the lead, starting a high-fidelity conversation that sounds and feels human.' 
    },
    { 
      id: '03', 
      title: 'AI QUALIFICATION', 
      desc: 'LaunchScript AI handles the conversation, answers FAQs, and qualifies the lead based on your specific business requirements.' 
    },
    { 
      id: '04', 
      title: 'JOB BOOKED', 
      desc: 'The appointment is placed directly on your calendar and bucketed into Positive/Negative outcomes for your review.' 
    },
  ];

  return (
    <div className="w-full py-10 sm:py-20 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="relative p-6 sm:p-12 md:p-20 rounded-[2rem] sm:rounded-[4rem] bg-white/[0.01] transition-all duration-700">
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-10 md:gap-12 relative items-start">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#32dee6]/10 to-transparent -translate-y-1/2 z-0 pointer-events-none"></div>
            
            {nodes.map((node, i) => (
              <React.Fragment key={node.id}>
                <Node 
                  id={node.id}
                  title={node.title}
                  desc={node.desc}
                  isActive={activeNode === node.id}
                  onHover={() => setActiveNode(node.id)}
                  onLeave={() => setActiveNode(null)}
                />
                {i < nodes.length - 1 && (
                  <div className="lg:hidden flex justify-center py-2">
                    <div className="w-[1px] h-8 sm:h-12 bg-gradient-to-b from-[#32dee6]/40 to-transparent"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-12 sm:mt-24 flex flex-col items-center justify-center gap-6 sm:gap-10 opacity-70 group/status">
            <div className="inline-flex items-center space-x-3 sm:space-x-4 px-6 sm:px-8 py-2.5 sm:py-3.5 rounded-full bg-black border border-white/5 mono text-[9px] sm:text-[11px] text-gray-500 font-black uppercase tracking-[0.3em] sm:tracking-[0.4em] transition-all group-hover/status:border-[#32dee6]/30">
              <span className="text-gray-400">Node</span>
              <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-[#32dee6] dot-pulse shadow-[0_0_10px_#32dee6]"></div>
              <span className="text-[#32dee6]">Active</span>
              <span className="text-white/10 mx-1 sm:mx-2">|</span>
              <span className="text-gray-600">99.9%</span>
            </div>
            
            <div className="mono text-[8px] sm:text-[10px] text-gray-700 tracking-[0.2em] sm:tracking-[0.3em] font-bold animate-pulse text-center">
              SYNC_PROTOCOL_ESTABLISHED
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallFlowVisualizer;
